import { useCallback, useEffect, useMemo } from "react";
import { Card, CardHeader } from "reactstrap";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { TableContext } from "../../../components/Table/context";
import { useAccess } from "../../../utils/hooks/access";
import { useElementHeight } from "../../../utils/hooks/useElementHeight";
import { useUser } from "../../../utils/hooks/user";
import { useMyTimeSheets } from "../api/useMyTimeSheets";
import { useOverview } from "../components/Topbar/context";
import Actions from "./Actions";
import Summary from "./Actions/Summary";
import TimeSheetTable from "../components/TimeSheetTable";
import History from "./Actions/HistoryDrawer";
import AddTimesheetNote from "../components/TimeSheetTable/AddTimesheetNote";
import CopyTimesheetModal from "./Actions/CopyTimesheetModal";
import { useDateFilters } from "../hooks/useDateFilters";
import { useTimeOffPlans } from "../hooks/useTimeOffPlans";
import { getResultList } from "../../../utils/helpers/response";
import { useDateIdMap } from "../components/TimeSheetTable/hooks/useDateIdMap";
import { useTimeSheetTable } from "../components/TimeSheetTable/hooks/useTable";
import { useBatchUpdate } from "./hooks/useBatchUpdate";
import { useDateActions } from "./hooks/useDateActions";
import { useEdit } from "./hooks/useEdit";
import { useRefetch } from "./hooks/useRefetch";
import { useRemoveActions } from "./hooks/useRemoveActions";
import { useSummary } from "./hooks/useSummary";
import { useColumns } from "../components/TimeSheetTable/useColumns";
import { useColumns as useTeamColumns } from "../Team/Table/useColumns";
import { useVisible } from "../../../utils/hooks/useVisible";
import { useDateTimesheetMap } from "../components/TimeSheetTable/hooks/useDateTimesheetMap";
import { usePayGroupItems } from "../components/TimeSheetTable/hooks/usePayGroupItems";
import { useFilters } from "../filtersContext";
import Table from "./Table";
import OtherProfileTable from "./OtherProfileTable";
import { useExpanded, useTable } from "react-table";

import { useColumnsVisibilityPreferences } from "../../Settings/Configuration/hooks/useColumnsVisibilityPreferences";
import { useColumnsPreferences } from "../../Settings/Configuration/hooks/useColumnsPreferences";
import { usePreferences } from "../../Settings/Configuration/hooks/usePreferences";
import useSWR from "swr";
import useApi from "../../../utils/api";
import { formatTimeSheetParams } from "../api/helpers";
import { useSortTimeSheets } from "../components/TimeSheetTable/hooks/useSortTimeSheets";
import { useMissPunchModal } from "./hooks/useMissPunchModal";
import { useInOutDateModal } from "../Team/Table/TimeSheetTable/hooks/useInOutDateModal";
import { useWaiveTimeSheet } from "../api/useWaiveTimeSheet";
import { useOverrides } from "../api/useOverrides";
import { useOvertimeDrawer } from "../Team/Actions/OvertimeDrawer/useOvertimeDrawer";
import { useSyncTimesheetModal } from "../components/TimeSheetTable/SyncTimesheetModal/useSyncTimesheetModal";
import { useManadatoryBreakWarning } from "../components/MandatoryBreakWarning/useMandatoryBreakWarning";

export const useMyTimeSheetUserOverall = (filters) => {
	const user = useUser();
	const { authGet } = useApi();

	const key = useMemo(() => {
		if (!user?.id) {
			return null;
		}
		return {
			url: "/time-sheet/my-team",
			params: formatTimeSheetParams({
				...filters,
				userId: user?.id,
			}),
		};
	}, [user?.id, filters]);

	const fetcher = useCallback(async ({ url, params }) => {
		const { result } = await authGet(url, { params });
		return result[0];
	}, [authGet]);

	return useSWR(key, fetcher);
}

const Content = () => {
	const { t } = useTranslation();
	const hideDailySummary = usePreferences("myTimesheet.hideDailySummary");
	const hideWeeklySummary = usePreferences("myTimesheet.hideWeeklySummary");
	const hideClockDatePicker = usePreferences("myTimesheet.timeSheet.hideClockDatePicker");
	const syncSequentialTimesheets = usePreferences("timeSheetGeneral.syncSequentialTimesheets");
	const showMandatoryBreakWarning = usePreferences("timeSheetGeneral.showMandatoryBreakWarning");

	const [filters] = useFilters();
	const { ref: actionsRef, height: actionsHeight } = useElementHeight();
	const { from, to, mode, setFilters: setDateFilters } = useDateFilters();

	const { fetch: baseFetchOverview } = useOverview();
	const fetchOverview = useCallback(
		(config) => {
			baseFetchOverview({
				url: "/time-sheet/overview",
				...config,
			});
		},
		[baseFetchOverview],
	);

	const {
		data: { result: baseData },
		scheduledHours,
		clockExceptions,
		isLoading: loadingTimeSheet,
		setTotalItems,
		setData,
		mutate,
		mutateScheduledHours,
		mutateClockExceptions,
		missPunchRequest: _missPunchRequest
	} = useMyTimeSheets({ from, to, filters });
	const data = useSortTimeSheets(baseData);

	const {
		data: user,
		isLoading: loadingUser,
		mutate: mutateUser
	} = useMyTimeSheetUserOverall({ from, to, filters });
	const refetchUser = useCallback(() => {
		mutateUser();
	}, [mutateUser]);

	const {
		data: overrides,
		mutate: mutateOverrides
	} = useOverrides(user?.id, from, to);

	const refetchTimeSheets = useCallback(() => {
		mutate();
		mutateScheduledHours();
		mutateClockExceptions();
		mutateOverrides();
	}, [mutate, mutateScheduledHours, mutateClockExceptions, mutateOverrides]);
	const { refetchOverview, refetchOverviewAndTimeSheets } = useRefetch({
		refetchUser,
		fetchOverview,
		refetchTimeSheets,
		filters,
	});

	const { modal: removeModal, remove } = useRemoveActions({
		refetchOverview: () => refetchOverviewAndTimeSheets(),
		setData,
		setTotalItems,
	});

	const { approve, unapprove, lock, unlock } = useDateActions(
		refetchOverviewAndTimeSheets,
	);

	const [openSyncTimesheetModal, syncTimesheetModal] = useSyncTimesheetModal();
	const { open: openMandatoryBreakWarning, modal: mandatoryBreakWarningModal } = useManadatoryBreakWarning();

	const {
		visible: editVisible,
		open: openEdit,
		close: closeEdit,
		selected: editSelected,
		loading: editLoading,
		error: editError,
		submit: editSubmit,
	} = useEdit({ refetch: refetchOverviewAndTimeSheets, isMyTimesheet: true });

	const {
		visible: summaryVisible,
		selected: summaryRange,
		openDaySummary,
		openAllSummary,
		close: closeSummary,
	} = useSummary();

	const {
		visible: historyVisible,
		selected: selectedTimesheet,
		open: openHistory,
		close: closeHistory,
	} = useVisible();

	const {
		visible: copyModalVisible,
		selected: selectedInfo,
		open: openCopyModal,
		close: closeCopyModal,
	} = useVisible();
	
	const { open: openOvertimeDrawer, drawer: overtimeDrawer } = useOvertimeDrawer({ isMyTimesheet: true });

	const { hasAccess: canViewRates } = useAccess("canViewMyRates");
	const { hasAccess: canCreate } = useAccess(
		"timeSheet.canCreateMyTimesheet",
	);
	const { hasAccess: canEdit } = useAccess("timeSheet.canCreateMyTimesheet" /**"timeSheet.canEditMyTimesheet" */);
	const { hasAccess: canViewMyOriginalClocks } = useAccess(
		"timeSheet.canViewMyOriginalClocks",
	);
	const { hasAccess: canViewHistory } = useAccess(
		"timeSheet.canViewHistoryMyTimesheet",
	);
	
	const { hasAccess: canViewExceptions } = useAccess(
		"timeSheet.canViewClockExceptionsMyTimesheet",
	);
	const { hasAccess: canChangeClockDate } = useAccess("timeSheet.canChangeClockDateMyTimesheet");

	const { waiveMyMealPremium: _waive } = useWaiveTimeSheet();
	const waive = useCallback((date, data) => {
		_waive(date, data, () => {
			refetchOverviewAndTimeSheets();
			toast.success(t("request-created-successfully"))
		});
	}, [refetchOverviewAndTimeSheets, _waive, t]);

	const missPunchRequest = useCallback((id, data) => {
		_missPunchRequest(id, data, () => {
			refetchOverviewAndTimeSheets();
			toast.success(t("request-created-successfully"))
		});
	}, [_missPunchRequest, refetchOverviewAndTimeSheets, t]);

	const { open: openMissPunchModal, modal: missPunchModal } = useMissPunchModal({ missPunchRequest });
	const { open: openInOutDateModal, modal: inOutDateModal } = useInOutDateModal();

	const dateIdMap = useDateIdMap({ data });
	const dateTimesheetMap = useDateTimesheetMap({ data });
	const columns = useColumns({
		user,
		scheduleInfo: scheduledHours,
		overrides,
		canCreate,
		canEdit,
		dateIdMap,
		dateTimesheetMap,
		approve,
		unapprove,
		lock,
		unlock,
		viewSummary: !hideDailySummary && openDaySummary,
		edit: editSubmit ? openEdit : null,
		view: (canViewMyOriginalClocks || canViewHistory || canViewExceptions) ? openHistory : undefined,
		remove,
		copy: openCopyModal,
		isMyTimesheet: true,
		clockExceptions,
		waive,
		openMissPunchModal,
		openInOutDateModal: (canChangeClockDate && !hideClockDatePicker) ? openInOutDateModal : undefined,
		openSyncTimesheetModal: syncSequentialTimesheets ? openSyncTimesheetModal : undefined,
		openMandatoryBreakWarning: showMandatoryBreakWarning ? openMandatoryBreakWarning : undefined,
		canViewRates,
	});

	const { submit: batchUpdateSubmit, loading: batchUpdateLoading } =
		useBatchUpdate({
			refetch: refetchOverviewAndTimeSheets,
			filters,
		});

	const payGroupItems = usePayGroupItems(user);
	const { data: profileTimeOffPlans } = useTimeOffPlans({ self: true });
	const table = useTimeSheetTable({
		user,
		columns: useColumnsPreferences(columns, "myTimesheet"),
		timeOffPlans: getResultList(profileTimeOffPlans),
		data,
		dateIdMap,
		batchUpdate: batchUpdateSubmit,
		submitting: batchUpdateLoading,
		payGroupItems,
		openOvertimeDrawer
	});

	const hiddenColumnsMap = useColumnsVisibilityPreferences("myTimesheet");
	useEffect(() => {
		if (hiddenColumnsMap) {
			table.setHiddenColumns(Object.keys(hiddenColumnsMap));
		}
	}, [table, hiddenColumnsMap]);

	useEffect(() => {
		const controller = new AbortController();
		fetchOverview({
			params: { from, to, filters },
			signal: controller.signal,
		});
		return () => controller.abort();
	}, [fetchOverview, from, to, filters]);

	useEffect(() => {
		if (user?.payGroup?.id && mode === "pay-group") {
			setDateFilters({ payGroup: user.payGroup.code });
		}
	}, [user, mode, setDateFilters]);

	const teamTable = useTable(
		{
			data: useMemo(() => {
				if (!user) {
					return [];
				}
				return [user];
			}, [user]),
			columns: useColumnsPreferences(useTeamColumns({ viewSummary: openAllSummary, canViewRates }), "timeSheetTeam"),
			defaultColumn: {
				hideable: true,
				alwaysVisible: false,
			},
			getRowId: useCallback(({ id }) => id, []),
			autoResetExpanded: false,
		},
		useExpanded,
	);

	useEffect(() => {
		teamTable.toggleAllRowsExpanded(true);
	}, [user?.id, teamTable]);

	const otherParams = useMemo(() => ({
		from,
		to,
		filters
	}), [from, to, filters]);

	return (
		<TableContext.Provider value={table}>
			<Card innerRef={actionsRef} className="mb-0 position-static">
				<CardHeader className="border-top p-3">
					<Actions
						columns={table.columns}
						selectedRowIds={table.state.selectedRowIds}
						viewSummary={!hideWeeklySummary && openAllSummary}
						user={user}
						filters={filters}
						approve={approve}
						unapprove={unapprove}
						lock={lock}
						unlock={unlock}
						remove={remove}
					/>
				</CardHeader>
			</Card>

			<div style={{ maxHeight: `calc(100% - ${actionsHeight}px)` }}>
				<TableContext.Provider value={teamTable}>
					<Table loading={loadingUser}>
						{(item) => {
							if (item.id === user.id) {
								return (
									<TableContext.Provider value={table}>
										<TimeSheetTable loading={loadingTimeSheet} isMyTimesheet={true} />
									</TableContext.Provider>
								);
							} else {
								return (
									<OtherProfileTable
										key={item.id}
										user={item}
										params={otherParams}
										viewSummary={!hideDailySummary ? openDaySummary : undefined}
										canViewRates={canViewRates}
										view={(canViewMyOriginalClocks || canViewHistory || canViewExceptions) ? openHistory : undefined}
										hiddenColumns={table.state.hiddenColumns || []}
									/>
								);
							}
						}}
					</Table>
				</TableContext.Provider>

				{editSubmit && (
					<AddTimesheetNote
						visible={editVisible}
						close={closeEdit}
						onFinish={editSubmit}
						error={editError}
						submitting={editLoading}
						values={editSelected}
					/>
				)}
			</div>

			{summaryVisible && (
				<Summary
					open={summaryVisible}
					close={closeSummary}
					user={user}
					range={summaryRange}
					filters={filters}
					canViewRates={canViewRates}
				/>
			)}

			{historyVisible && (
				<History
					open={historyVisible}
					close={closeHistory}
					user={user}
					selected={selectedTimesheet}
					isMyTimesheet={true}
					canViewRates={canViewRates}
					overrides={overrides}
					refetch={() => refetchOverviewAndTimeSheets()}
				/>
			)}

			{copyModalVisible && (
				<CopyTimesheetModal
					isOpen={copyModalVisible}
					close={closeCopyModal}
					timesheet={selectedInfo}
					user={user}
					dateTimesheetMap={dateTimesheetMap}
					refetch={refetchOverviewAndTimeSheets}
					filters={{ filters }}
					canViewRates={canViewRates}
				/>
			)}

			{removeModal}
			{missPunchModal}
			{inOutDateModal}
			{overtimeDrawer}
			{syncTimesheetModal}
			{mandatoryBreakWarningModal}
		</TableContext.Provider>
	);
};

export default Content;
